import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// API 封装：自动带 Token
export const apiFetch = async (path, opts = {}) => {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(path, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      // 401 认证失败，跳转登录页
      if (res.status === 401) {
        throw new Error('认证失败，请重新登录');
      }
      const err = new Error(data.error || `HTTP ${res.status}`);
      err.status = res.status; // 暴露 HTTP 状态码，调用方可精确分支（如 404 自动恢复）
      throw err;
    }

    return data.data;
  } catch (e) {
    // 网络层错误处理
    if (e instanceof TypeError) {
      throw new Error('网络错误，请检查连接');
    }
    throw e;
  }
};

// 导航菜单（供布局组件读取）
export const navItemsStore = ref([]);

// 插件注册表：汇总每个插件的元数据及其前端页面/路由清单（供"插件管理"页展示）。
// 形如 [{ id, name, version, icon, frontendRemote, error?, routes: [{ path, name, title }] }]
export const pluginRegistry = ref([]);

// 全局 API 错误处理器
export const handleApiError = async (error) => {
  if (error.message && error.message.includes('认证失败')) {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  throw error;
};

// 包装 API 调用，自动处理 401
export const apiFetchSafe = async (path, opts = {}) => {
  try {
    return await apiFetch(path, opts);
  } catch (error) {
    if (error.message && error.message.includes('认证失败')) {
      await handleApiError(error);
    }
    throw error;
  }
};

// 动态加载 MF remote container
// remoteEntryUrl: /plugins/pve-mgr/remote.js
async function loadRemoteEntry(remoteEntryUrl, remoteName) {
  // 初始化共享作用域（只执行一次）
  await __webpack_init_sharing__('default');

  // 已初始化过的 container 直接复用
  let container = window[remoteName];
  if (!container) {
    // 注入 script 标签加载 remote.js
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = remoteEntryUrl;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`加载 ${remoteEntryUrl} 失败`));
      document.head.appendChild(script);
    });
    container = window[remoteName];
    if (!container) {
      throw new Error(`remote ${remoteName} 未在 window 上注册`);
    }
  }

  // 初始化 container（注入共享依赖）
  await container.init(__webpack_share_scopes__.default);

  // 获取暴露的模块
  const factory = await container.get('./entry');
  return factory();
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('token') || '');
  const username = ref(localStorage.getItem('username') || '');
  const role = ref(localStorage.getItem('role') || '');
  const plugins = ref([]);
  const pluginsLoaded = ref(false);
  const isLoggedIn = computed(() => !!token.value);
  const isAdmin = computed(() => role.value === 'admin');

  async function login(user, pass) {
    const data = await apiFetch('/api/usermgmt/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username: user, password: pass }),
    });
    token.value = data.token;
    username.value = data.username;
    role.value = data.role || '';
    localStorage.setItem('token', data.token);
    localStorage.setItem('username', data.username);
    localStorage.setItem('role', data.role || '');
  }

  function logout() {
    token.value = '';
    username.value = '';
    role.value = '';
    plugins.value = [];
    pluginsLoaded.value = false;
    navItemsStore.value = [];
    pluginRegistry.value = [];
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
  }

  async function loadPlugins(router) {
    const list = await apiFetchSafe('/api/plugins/manifest');
    const items = [];
    const registry = [];

    for (const p of list) {
      // 登记每个插件（含纯后端插件，frontendRemote 为空表示无前端）
      const entry = {
        id: p.id,
        name: p.name,
        version: p.version,
        icon: p.icon,
        frontendRemote: p.frontendRemote || '',
        routes: [],
      };

      if (!p.frontendRemote) {
        registry.push(entry);
        continue;
      }

      try {
        // remote name: pve-mgr → pve_mgr（MF container 变量名不能含连字符）
        const remoteName = p.id.replace(/-/g, '_');
        const mod = await loadRemoteEntry(p.frontendRemote, remoteName);

        // 执行 setup，注入 host 上下文
        const hostCtx = {
          api: apiFetch,
          addNav: (item) => items.push({ ...item, pluginId: p.id }),
          getRegistry: () => pluginRegistry.value,
        };
        if (mod.setup) mod.setup(hostCtx);

        // 注册路由到 root 布局下，并记录路由清单（剔除不可序列化的 component）
        if (mod.routes) {
          for (const route of mod.routes) {
            router.addRoute('root', {
              path: route.path,
              name: route.name,
              component: route.component,
              meta: { title: route.title || p.name },
            });
            entry.routes.push({
              path: route.path,
              name: route.name,
              title: route.title,
            });
          }
        }

        registry.push(entry);
      } catch (e) {
        console.error(`[host] 加载插件 ${p.id} 失败:`, e);
        entry.error = e?.message || String(e);
        registry.push(entry);
      }
    }

    navItemsStore.value = items;
    pluginRegistry.value = registry;
    plugins.value = list;
    pluginsLoaded.value = true;
  }

  return { token, username, role, isAdmin, plugins, pluginsLoaded, isLoggedIn, login, logout, loadPlugins };
});
